import type { Notification, ReportRegistryResponse, StoredNotification } from '@cabgo/shared';
import { Avatar } from '@cabgo/ui';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  BellOff,
  CalendarDays,
  Car,
  ChartPie,
  FileChartColumn,
  Grid2X2,
  LayoutDashboard,
  LogOut,
  MapPin,
  Menu,
  Radio,
  Route,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { NotificationDrawer } from '../components/notifications/NotificationDrawer';
import { NotificationToast } from '../components/notifications/NotificationToast';
import { RouteBoundary } from '../components/RouteBoundary';
import { useOperatorNotifications } from '../hooks/useOperatorNotifications';
import { useWebPush } from '../hooks/useWebPush';
import { setLocale } from '../i18n';
import { apiGet, getActiveOrg } from '../lib/api';
import { useAuth } from '../lib/auth';
import { hasOpenRegistryReport, reportRegistryPath } from '../pages/reports/reportAdapters';

interface RailItem {
  to: string;
  icon: ReactNode;
  labelKey: string;
}

const MOBILE_NAV_ITEMS: RailItem[] = [
  { to: '/tracking', icon: <Grid2X2 size={22} />, labelKey: 'tracking' },
  { to: '/trips', icon: <CalendarDays size={22} />, labelKey: 'trips' },
  { to: '/passengers', icon: <Users size={22} />, labelKey: 'passengers' },
  { to: '/drivers', icon: <Car size={22} />, labelKey: 'drivers' },
];

const RAIL_ITEMS: RailItem[] = [
  { to: '/', icon: <LayoutDashboard size={21} />, labelKey: 'dashboard' },
  { to: '/analytics', icon: <ChartPie size={21} />, labelKey: 'dashboard:menuAnalytics' },
  { to: '/tracking', icon: <MapPin size={21} />, labelKey: 'tracking' },
  { to: '/trips', icon: <Route size={21} />, labelKey: 'trips' },
  { to: '/passengers', icon: <Users size={21} />, labelKey: 'passengers' },
  { to: '/drivers', icon: <Car size={21} />, labelKey: 'drivers' },
  // dev/QA dispatch simulation — non-production environments only
  ...(import.meta.env.MODE !== 'production'
    ? [{ to: '/dispatch-sim', icon: <Radio size={21} />, labelKey: 'dispatchSim' }]
    : []),
  { to: '/settings', icon: <Settings size={21} />, labelKey: 'settings' },
];

/** Web console shell: dark navy icon rail (64px) + fluid content. */
export function AppShell() {
  const { t, i18n } = useTranslation('nav');
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const superAdmin = me?.role === 'SUPER_ADMIN';
  const activeProviderId = getActiveOrg() ?? me?.providerId ?? null;
  const registryQuery = useQuery({
    queryKey: ['reports-registry', me?.id ?? 'anonymous', me?.providerId ?? 'no-provider', activeProviderId],
    queryFn: () => apiGet<ReportRegistryResponse>(reportRegistryPath(superAdmin, activeProviderId)),
    enabled: Boolean(me),
    staleTime: 60_000,
  });
  const railItems = [
    ...RAIL_ITEMS.slice(0, 2),
    ...(hasOpenRegistryReport(registryQuery.data)
      ? [
          {
            to: '/reports/service_job_detail',
            icon: <FileChartColumn size={21} />,
            labelKey: 'reports:navLabel',
          },
        ]
      : []),
    ...RAIL_ITEMS.slice(2),
  ];

  const [drawerOpen, setDrawerOpen] = useState(false);

  const { notifications, unreadCount, isMuted, toggleMute, markRead, markAllRead, toast, dismissToast } =
    useOperatorNotifications();
  const webPush = useWebPush(me?.id);

  const navigateToTrip = (tripId?: string | null) => {
    if (tripId) {
      navigate(`/trips/${tripId}`);
    }
  };

  const handleSelectNotification = (item: StoredNotification) => {
    void markRead(item.id);
    setDrawerOpen(false);
    navigateToTrip(item.tripId);
  };

  const handleToastClick = (item: Notification) => {
    dismissToast();
    navigateToTrip(item.tripId);
  };

  const mobileNavigate = () => setMobileNavOpen(false);

  return (
    <div className="shell">
      <nav className="rail" aria-label={t('mainNav')}>
        <div className="rail__brand" title="Cab Go">
          C
        </div>
        <button
          type="button"
          className="rail__mobile-toggle"
          style={{ display: 'none' }}
          aria-label={mobileNavOpen ? t('common:close') : t('common:menu')}
          aria-expanded={mobileNavOpen}
          onClick={() => setMobileNavOpen((open) => !open)}
        >
          {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
          <span className="rail__mobile-toggle-label">{t('common:menu')}</span>
        </button>
        {railItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            title={t(item.labelKey)}
            className={({ isActive }) => `rail__item ${isActive ? 'rail__item--active' : ''}`}
          >
            {item.icon}
            <span className="rail__label">{t(item.labelKey)}</span>
          </NavLink>
        ))}
        <div className="rail__spacer" />
        <button
          type="button"
          className={`rail__item rail__item--has-badge ${drawerOpen ? 'rail__item--active' : ''}`}
          title={isMuted ? 'การแจ้งเตือน (ปิดเสียง)' : 'การแจ้งเตือน'}
          aria-label={isMuted ? 'การแจ้งเตือน (ปิดเสียง)' : 'การแจ้งเตือน'}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <Bell size={21} />
          {isMuted && (
            <span className="rail__item-flag" aria-hidden="true">
              <BellOff size={11} strokeWidth={2.5} />
            </span>
          )}
          {unreadCount > 0 && <span className="rail__badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
          <span className="rail__label">แจ้งเตือน</span>
        </button>
        <button
          type="button"
          className="rail__item"
          title={t('common:language')}
          onClick={() => setLocale(i18n.language === 'th' ? 'en' : 'th')}
        >
          <span style={{ fontSize: 12, fontWeight: 700 }}>{i18n.language === 'th' ? 'EN' : 'ไทย'}</span>
          <span className="rail__label">{t('common:language')}</span>
        </button>
        {me && (
          <div className="rail__avatar" style={{ padding: '6px 0' }}>
            <Avatar name={me.firstName} size={32} />
          </div>
        )}
        <button
          type="button"
          className="rail__item"
          title={t('logout')}
          onClick={async () => {
            await logout();
            navigate('/login');
          }}
        >
          <LogOut size={20} />
          <span className="rail__label">{t('logout')}</span>
        </button>
      </nav>
      <div className="main">
        <RouteBoundary>
          <Outlet />
        </RouteBoundary>
      </div>
      <nav className="mobile-bottom-nav" aria-label={t('common:menu')}>
        {MOBILE_NAV_ITEMS.slice(0, 2).map((item) => (
          <NavLink
            key={`bottom-${item.to}`}
            to={item.to}
            className="mobile-bottom-nav__item"
            title={t(item.labelKey)}
          >
            {item.icon}
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className="mobile-bottom-nav__menu"
          onClick={() => setMobileNavOpen(true)}
          aria-label={t('common:menu')}
        >
          <span className="mobile-bottom-nav__logo">C</span>
          <span>{t('common:menu')}</span>
        </button>
        {MOBILE_NAV_ITEMS.slice(2).map((item) => (
          <NavLink
            key={`bottom-${item.to}`}
            to={item.to}
            className="mobile-bottom-nav__item"
            title={t(item.labelKey)}
          >
            {item.icon}
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
      {mobileNavOpen && (
        <>
          <div className="mobile-nav-overlay" aria-hidden="true" onClick={mobileNavigate} />
          <div className="mobile-nav-drawer" role="dialog" aria-label={t('common:menu')}>
            <div className="mobile-nav-drawer__header">
              <strong>{t('common:menu')}</strong>
              <button
                type="button"
                className="mobile-nav-drawer__close"
                onClick={mobileNavigate}
                aria-label={t('common:close')}
              >
                <X size={20} />
              </button>
            </div>
            {railItems.map((item) => (
              <NavLink
                key={`mobile-${item.to}`}
                to={item.to}
                end={item.to === '/'}
                title={t(item.labelKey)}
                onClick={mobileNavigate}
                className={({ isActive }) =>
                  `mobile-nav-drawer__item ${isActive ? 'mobile-nav-drawer__item--active' : ''}`
                }
              >
                {item.icon}
                <span>{t(item.labelKey)}</span>
              </NavLink>
            ))}
            <button
              type="button"
              className="mobile-nav-drawer__item"
              onClick={() => {
                setLocale(i18n.language === 'th' ? 'en' : 'th');
                mobileNavigate();
              }}
            >
              <span>{i18n.language === 'th' ? 'EN' : 'ไทย'}</span>
              <span>{t('common:language')}</span>
            </button>
            <button
              type="button"
              className="mobile-nav-drawer__item"
              onClick={() => {
                mobileNavigate();
                setDrawerOpen(true);
              }}
            >
              <Bell size={20} />
              <span>การแจ้งเตือน {unreadCount > 0 ? `(${unreadCount})` : ''}</span>
            </button>
            <button
              type="button"
              className="mobile-nav-drawer__item"
              onClick={async () => {
                await logout();
                mobileNavigate();
                navigate('/login');
              }}
            >
              <LogOut size={20} />
              <span>{t('logout')}</span>
            </button>
          </div>
        </>
      )}
      <NotificationDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        onMarkRead={markRead}
        onMarkAllRead={markAllRead}
        onSelectNotification={handleSelectNotification}
        webPushState={webPush.state}
        onEnableWebPush={webPush.enable}
        onDisableWebPush={webPush.disable}
      />
      <NotificationToast toast={toast} onDismiss={dismissToast} onClick={handleToastClick} />
    </div>
  );
}

/** Standard page header: title + count + actions (teal + button etc). */
export function PageHeader({
  title,
  count,
  children,
}: {
  title: ReactNode;
  count?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="header">
      <h1 className="header__title">{title}</h1>
      {count !== undefined && <span className="header__count">{count}</span>}
      <div className="header__spacer" />
      {children}
    </header>
  );
}
